import type { ClinicClock } from "@/lib/clinic-clock";

import { EventStatus } from "@/types/enums/event";
import type { EventDetail } from "@/types/event";

// The patient sidebar's appointment lists, and which of their appointments
// show a checklist. Pylon #9220: once a visit ran past its booked end, the
// sidebar dropped it and listed next week's visit with its checklist open,
// and ticking that checklist checked the future visit in, marked it paid and
// completed it.

export type DayClock = Pick<ClinicClock, "today" | "dayOf">;

export type SidebarVisit = Pick<EventDetail, "start" | "status"> &
  Partial<Pick<EventDetail, "end">> & {
    location?: { id?: string | null } | null;
  };

/** Each appointment's own clock, so "today" is today where the visit happens. */
export type AppointmentClockOf = (appointment: SidebarVisit) => DayClock;

/** Earliest start first. */
export const byStartAsc = (
  a: Pick<EventDetail, "start">,
  b: Pick<EventDetail, "start">,
) => new Date(a.start).getTime() - new Date(b.start).getTime();

// A cancelled visit or a no-show is not a visit anyone is working on.
const isAttendedVisit = (appointment: SidebarVisit) =>
  appointment.status !== EventStatus.CANCELLED &&
  appointment.status !== EventStatus.NO_SHOW;

/** An appointment on today's date at its location, whatever its status. */
export function isOnToday(
  appointment: SidebarVisit,
  clockOf: AppointmentClockOf,
) {
  if (!appointment.start) return false;
  const clock = clockOf(appointment);
  return clock.dayOf(appointment.start) === clock.today();
}

/** A visit on today's date at its location, not cancelled or a no-show. */
export function isVisitToday(
  appointment: SidebarVisit,
  clockOf: AppointmentClockOf,
) {
  return isAttendedVisit(appointment) && isOnToday(appointment, clockOf);
}

function isOnLaterDay(appointment: SidebarVisit, clockOf: AppointmentClockOf) {
  if (!appointment.start) return false;
  const clock = clockOf(appointment);
  return clock.dayOf(appointment.start) > clock.today();
}

/** Whether any of the patient's visits is a visit today. */
export function patientHasVisitToday(
  visits: readonly SidebarVisit[] | null | undefined,
  clockOf: AppointmentClockOf,
) {
  return (visits ?? []).some((visit) => isVisitToday(visit, clockOf));
}

/**
 * The visits the patient sidebar lists when no appointment is selected,
 * earliest first: every visit from today on, so a visit that runs past its
 * booked end stays listed instead of the next visit taking its place, plus
 * anything that has not ended yet. A cancelled visit or a no-show from earlier
 * today drops off once it has ended, as before.
 */
export function getSidebarVisitsFromToday<T extends SidebarVisit>(
  events: readonly T[] | null | undefined,
  clockOf: AppointmentClockOf,
  now: Date = new Date(),
): T[] {
  return (events ?? [])
    .filter((event) => {
      if (event.end && new Date(event.end) >= now) return true;
      if (!event.start || !isAttendedVisit(event)) return false;
      const clock = clockOf(event);
      return clock.dayOf(event.start) >= clock.today();
    })
    .sort(byStartAsc);
}

// An open visit someone may still work on: a no-show or a cancelled visit is
// not one, so it never holds back a later visit's checklist.
const isNonCompleteChecklistCandidate = (event: EventDetail) =>
  event.status !== EventStatus.COMPLETED && isAttendedVisit(event);

/**
 * Whether a sidebar appointment list shows this appointment's checklist.
 *
 * A cancelled visit or a no-show never gets one. While the patient has a
 * visit today (`hasVisitToday`, from every visit the patient has, see
 * `patientHasVisitToday`), a later day's checklist stays hidden: that visit's
 * own appointment on the calendar still opens it. Otherwise only visits up to
 * the first open one in `renderedAppointments` get one. An open visit stops
 * counting once a later visit has started: a visit earlier today that nobody
 * completed cannot hide the one in progress, while a visit running past its
 * booked end still holds back the next one's checklist.
 */
export function shouldShowSidebarChecklist(
  appointment: EventDetail,
  renderedAppointments: EventDetail[],
  laterDays: {
    hasVisitToday: boolean;
    clockOf: AppointmentClockOf;
    now?: Date;
  },
) {
  const nowMs = (laterDays.now ?? new Date()).getTime();
  if (!isAttendedVisit(appointment)) return false;
  if (laterDays.hasVisitToday && isOnLaterDay(appointment, laterDays.clockOf)) {
    return false;
  }
  if (renderedAppointments.length <= 1) return true;
  if (!appointment.start) return true;

  const startMs = (event: EventDetail) => new Date(event.start).getTime();
  const isSupersededByLaterVisit = (event: EventDetail) =>
    renderedAppointments.some(
      (later) =>
        later.start &&
        isAttendedVisit(later) &&
        startMs(later) > startMs(event) &&
        startMs(later) <= nowMs,
    );

  const firstNonCompleteAppointment =
    renderedAppointments.reduce<EventDetail | null>((firstEvent, event) => {
      if (
        !event.start ||
        !isNonCompleteChecklistCandidate(event) ||
        isSupersededByLaterVisit(event)
      ) {
        return firstEvent;
      }
      if (!firstEvent?.start) return event;

      return new Date(event.start).getTime() <
        new Date(firstEvent.start).getTime()
        ? event
        : firstEvent;
    }, null);

  if (!firstNonCompleteAppointment?.start) return true;

  return (
    new Date(appointment.start).getTime() <=
    new Date(firstNonCompleteAppointment.start).getTime()
  );
}
